/**
 * Sysadmin Audit → Compliance Exports (Phase 5 PR-F v1) — COMPLIANCE-GRADE.
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.7 + Phase 5 architect §2.5 + §5.2 R-7:
 *
 *   Produces signed audit bundles per compliance framework. Each bundle
 *   is a tar.gz on the local FS at /var/argusai/audit-exports/ (OQ-4
 *   founder decision, air-gap friendly), signed with the platform
 *   Ed25519 witness key, including SHA-256 anchors of the first + last
 *   audit row.
 *
 * UX invariants:
 *   - 5-second polling while any bundle is queued or running.
 *   - Date range picker enforces the 90-day cap client-side (with a
 *     surfaced server-side 422 fallback for clock drift).
 *   - Download button enabled only when status='complete'.
 *   - "Verify signature" link opens a Dialog showing the verification
 *     recipe + anchor hashes that a regulator would re-derive.
 *   - Failed bundles show their error_message inline.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Download,
  Plus,
  ShieldCheck,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Inbox,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { PageError } from '@/components/ui/page-error';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAuditExports,
  useCreateAuditExport,
  AUDIT_EXPORT_FRAMEWORK_META,
  AUDIT_EXPORT_MAX_RANGE_DAYS,
  type AuditExportFramework,
  type AuditExportScope,
  type AuditExportRow,
  type CreateAuditExportInput,
} from '@/lib/use-sysadmin-platform-audit';
import { StepUpReasonDialog } from '@/components/ui/step-up-reason-dialog';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/audit/exports')({
  component: ComplianceExportsPage,
});

// ─── Page ────────────────────────────────────────────────────────────────

function ComplianceExportsPage() {
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } =
    useAuditExports();
  const [creating, setCreating] = useState(false);
  const [verifying, setVerifying] = useState<AuditExportRow | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const items = data?.items ?? [];

  const counts = useMemo(
    () => ({
      total: items.length,
      queued: items.filter((b) => b.status === 'queued').length,
      running: items.filter((b) => b.status === 'running').length,
      complete: items.filter((b) => b.status === 'complete').length,
      failed: items.filter((b) => b.status === 'failed').length,
    }),
    [items]
  );

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load audit exports"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Download className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Compliance Audit Exports
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs font-mono">§3.7</Badge>
              <Badge variant="secondary" className="text-xs">PR-F</Badge>
              <span className="text-xs text-muted-foreground">
                Signed bundles per framework. Bundles live on local FS;
                90-day per-bundle range cap.
              </span>
              {dataUpdatedAt > 0 && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total" value={String(counts.total)} icon={Download} tone="default" />
        <StatTile label="Queued" value={String(counts.queued)} icon={Inbox} tone="muted" />
        <StatTile label="Running" value={String(counts.running)} icon={Loader2} tone={counts.running > 0 ? 'warning' : 'muted'} />
        <StatTile label="Complete" value={String(counts.complete)} icon={CheckCircle2} tone={counts.complete > 0 ? 'success' : 'muted'} />
        <StatTile label="Failed" value={String(counts.failed)} icon={AlertTriangle} tone={counts.failed > 0 ? 'destructive' : 'muted'} />
      </div>

      {/* ── Table ── */}
      {isLoading && items.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading audit exports…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Download}
          title="No audit exports yet"
          body="Generate a bundle to produce a tamper-evident, signed audit export for a compliance framework."
        />
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Framework</th>
                <th className="px-3 py-2 font-medium">Range</th>
                <th className="px-3 py-2 font-medium">Scope</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Events</th>
                <th className="px-3 py-2 font-medium text-right">Size</th>
                <th className="px-3 py-2 font-medium">Signed</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <BundleRow
                  key={b.id}
                  bundle={b}
                  onVerify={() => setVerifying(b)}
                  onDownloadError={setDownloadError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create dialog ── */}
      {creating && (
        <CreateExportDialog
          open={creating}
          onOpenChange={setCreating}
          onSuccess={() => refetch()}
        />
      )}

      {/* ── Verify dialog ── */}
      {verifying && (
        <VerifyDialog
          open={!!verifying}
          onOpenChange={(v) => !v && setVerifying(null)}
          bundle={verifying}
        />
      )}

      {/* ── Download error toast (inline) ── */}
      {downloadError && (
        <div className="fixed bottom-4 right-4 max-w-md rounded-md border bg-destructive/10 border-destructive/30 px-4 py-3 text-sm shadow-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-destructive">Download failed</div>
              <div className="text-xs text-muted-foreground mt-1">
                {downloadError}
              </div>
            </div>
            <button
              onClick={() => setDownloadError(null)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bundle row ──────────────────────────────────────────────────────────

function BundleRow({
  bundle,
  onVerify,
  onDownloadError,
}: {
  bundle: AuditExportRow;
  onVerify: () => void;
  onDownloadError: (msg: string) => void;
}) {
  const meta = AUDIT_EXPORT_FRAMEWORK_META[bundle.framework];
  const fromLabel = new Date(bundle.fromTs).toLocaleDateString();
  const toLabel = new Date(bundle.toTs).toLocaleDateString();
  const sizeLabel = bundle.bundleSizeBytes
    ? formatBytes(bundle.bundleSizeBytes)
    : '—';
  const eventLabel = bundle.eventCount?.toLocaleString() ?? '—';
  const [reasonOpen, setReasonOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (stepUpReason: string) => {
    if (bundle.status !== 'complete') return;
    setDownloading(true);
    try {
      // apiClient.get returns parsed JSON by default; we need the raw stream as
      // a Blob for file download. requestRaw bypasses JSON parsing AND lets us
      // attach the tier-1 X-Step-Up-Reason header the download endpoint requires
      // (platform audit 2026-06-10 — the prior bare call 400'd REASON_REQUIRED).
      const response = await apiClient.requestRaw(
        `/admin/platform/audit/exports/${bundle.id}/download`,
        { headers: { 'X-Step-Up-Reason': stepUpReason } }
      );
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(
          `Download returned ${response.status}: ${body.slice(0, 200)}`
        );
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // #1128 — Bundle is a gzipped marker-delimited proprietary
      // container, not a POSIX tar archive. Filename now reflects that
      // so a regulator running `tar -xzf` does not get a confusing
      // failure. Prefer the server-supplied downloadKey when present
      // so pre-existing bundles serve under their original name.
      a.download = bundle.downloadKey ?? `${bundle.id}.argusai-bundle.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setReasonOpen(false);
    } catch (err) {
      onDownloadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
    <tr className="border-t hover:bg-muted/30 transition-colors">
      <td className="px-3 py-2.5">
        <div className="font-medium">{meta.label}</div>
        <div className="text-xs text-muted-foreground" title={meta.description}>
          {meta.description}
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs">
        <div className="font-mono">{fromLabel}</div>
        <div className="font-mono text-muted-foreground">→ {toLabel}</div>
      </td>
      <td className="px-3 py-2.5 text-xs">
        {bundle.scope === 'platform' ? (
          <Badge variant="outline" className="text-[10px]">Platform</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">
            Tenant
          </Badge>
        )}
        {bundle.tenantId && (
          <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
            {bundle.tenantId.slice(0, 8)}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        <StatusBundle status={bundle.status} />
        {bundle.status === 'failed' && bundle.errorMessage && (
          <div className="text-[10px] text-destructive mt-1 max-w-xs truncate" title={bundle.errorMessage}>
            {bundle.errorMessage}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">{eventLabel}</td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">{sizeLabel}</td>
      <td className="px-3 py-2.5 text-xs">
        {bundle.signedAt ? (
          <span className="inline-flex items-center gap-1 text-emerald-600">
            <ShieldCheck className="h-3 w-3" />
            <span className="font-mono">
              {new Date(bundle.signedAt).toLocaleDateString()}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReasonOpen(true)}
            disabled={bundle.status !== 'complete' || downloading}
            title={
              bundle.status === 'complete'
                ? 'Download signed bundle (requires an audit reason)'
                : `Bundle is ${bundle.status}; download disabled`
            }
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onVerify}
            disabled={bundle.status !== 'complete'}
            title="Show verification recipe"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
    {reasonOpen && (
      <StepUpReasonDialog
        open
        onOpenChange={(o) => !o && setReasonOpen(false)}
        title="Audit reason for bundle download"
        description="Tier-1 step-up. Downloading a signed compliance bundle releases audit evidence. State the audit reason."
        submitLabel={downloading ? 'Downloading…' : 'Download bundle'}
        submitting={downloading}
        onConfirm={handleDownload}
      />
    )}
    </>
  );
}

function StatusBundle({ status }: { status: AuditExportRow['status'] }) {
  switch (status) {
    case 'queued':
      return <StatusPill status="muted" compact icon={Inbox}>Queued</StatusPill>;
    case 'running':
      return (
        <StatusPill status="info" compact icon={Loader2}>
          Running
        </StatusPill>
      );
    case 'complete':
      return (
        <StatusPill status="healthy" compact icon={CheckCircle2}>
          Complete
        </StatusPill>
      );
    case 'failed':
      return (
        <StatusPill status="destructive" compact icon={AlertTriangle}>
          Failed
        </StatusPill>
      );
  }
}

// ─── Create dialog ───────────────────────────────────────────────────────

function CreateExportDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => void;
}) {
  const [framework, setFramework] =
    useState<AuditExportFramework>('platform_full');
  const [scope, setScope] = useState<AuditExportScope>('platform');
  const [tenantId, setTenantId] = useState('');
  const [fromDate, setFromDate] = useState(
    isoDateOffset(-7) // default last 7 days
  );
  const [toDate, setToDate] = useState(isoDateOffset(0));
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Queueing an export is a tier-1 step-up: stage the validated input, then
  // collect the audit reason before firing the mutation.
  const [reasonOpen, setReasonOpen] = useState(false);
  const [pendingInput, setPendingInput] = useState<CreateAuditExportInput | null>(null);

  const create = useCreateAuditExport();

  const spanDays = Math.round(
    (new Date(toDate).getTime() - new Date(fromDate).getTime()) /
      (24 * 3600 * 1000)
  );
  const rangeInvalid =
    spanDays <= 0 || spanDays > AUDIT_EXPORT_MAX_RANGE_DAYS;
  const tenantInvalid = scope === 'tenant' && !tenantId.match(/^[0-9a-f-]{36}$/);
  const canSubmit = !rangeInvalid && !tenantInvalid && !create.isPending;

  const handleSubmit = () => {
    setSubmitError(null);
    // fromDate/toDate are dates (no time). Treat fromDate as start-of-day UTC
    // and toDate as end-of-day UTC so the range is inclusive in human terms.
    const fromTs = new Date(`${fromDate}T00:00:00.000Z`).toISOString();
    const toTs = new Date(`${toDate}T23:59:59.999Z`).toISOString();
    setPendingInput({
      framework,
      fromTs,
      toTs,
      scope,
      tenantId: scope === 'tenant' ? tenantId : null,
    });
    setReasonOpen(true);
  };

  const handleConfirmReason = (stepUpReason: string) => {
    if (!pendingInput) return;
    create.mutate(
      { input: pendingInput, stepUpReason },
      {
        onSuccess: () => {
          setReasonOpen(false);
          onSuccess();
          onOpenChange(false);
        },
        onError: (err) => {
          setReasonOpen(false);
          setSubmitError(err instanceof Error ? err.message : String(err));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New compliance export</DialogTitle>
          <DialogDescription>
            Queues a signed audit bundle for the selected framework + range.
            Generation runs asynchronously; the row will appear in the table
            as <em>queued</em> → <em>running</em> → <em>complete</em>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Framework <span className="text-red-500">*</span>
            </Label>
            <Select
              value={framework}
              onValueChange={(v) => setFramework(v as AuditExportFramework)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(AUDIT_EXPORT_FRAMEWORK_META) as Array<
                  [AuditExportFramework, (typeof AUDIT_EXPORT_FRAMEWORK_META)[AuditExportFramework]]
                >).map(([k, meta]) => (
                  <SelectItem key={k} value={k}>
                    {meta.label} — {meta.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                From <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                To <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div
            className={cn(
              'rounded-md border p-2.5 text-xs',
              rangeInvalid
                ? 'border-destructive/40 bg-destructive/5 text-destructive'
                : 'border-muted-foreground/20 bg-muted/30'
            )}
          >
            <span className="font-mono">{spanDays}</span> days
            {spanDays > AUDIT_EXPORT_MAX_RANGE_DAYS && (
              <> — exceeds the {AUDIT_EXPORT_MAX_RANGE_DAYS}-day cap. Span multiple bundles instead.</>
            )}
            {spanDays <= 0 && <> — range must be positive.</>}
            {!rangeInvalid && <> (max {AUDIT_EXPORT_MAX_RANGE_DAYS})</>}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Scope</Label>
            <Select
              value={scope}
              onValueChange={(v) => setScope(v as AuditExportScope)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="platform">Platform (all tenants)</SelectItem>
                <SelectItem value="tenant">Single tenant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scope === 'tenant' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                Tenant ID <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="00000000-0000-0000-0000-000000000000"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                className="font-mono text-xs"
              />
              {tenantInvalid && tenantId.length > 0 && (
                <p className="text-[10px] text-destructive">
                  Must be a valid UUID.
                </p>
              )}
            </div>
          )}

          {submitError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending && (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            )}
            Queue export
          </Button>
        </DialogFooter>
      </DialogContent>

      {reasonOpen && (
        <StepUpReasonDialog
          open
          onOpenChange={(o) => !o && setReasonOpen(false)}
          title="Audit reason for compliance export"
          description="Tier-1 step-up. Queueing a signed compliance bundle releases audit evidence. State the audit reason."
          submitLabel={create.isPending ? 'Queueing…' : 'Queue export'}
          submitting={create.isPending}
          onConfirm={handleConfirmReason}
        />
      )}
    </Dialog>
  );
}

// ─── Verify dialog ───────────────────────────────────────────────────────

function VerifyDialog({
  open,
  onOpenChange,
  bundle,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  bundle: AuditExportRow;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Verify bundle signature
          </DialogTitle>
          <DialogDescription>
            A regulator can re-derive these values from the bundle file +
            platform public key to confirm tamper-evidence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-xs">
          <KeyValue label="Bundle ID" value={bundle.id} mono />
          <KeyValue
            label="Framework"
            value={AUDIT_EXPORT_FRAMEWORK_META[bundle.framework].label}
          />
          <KeyValue
            label="Range"
            value={`${new Date(bundle.fromTs).toLocaleDateString()} → ${new Date(bundle.toTs).toLocaleDateString()}`}
          />
          <KeyValue
            label="Event count"
            value={bundle.eventCount?.toLocaleString() ?? '—'}
          />

          <div className="rounded-md border border-muted-foreground/20 bg-muted/20 p-3 space-y-2">
            <div className="font-semibold text-[10px] uppercase tracking-wide">
              First-row anchor
            </div>
            <div className="font-mono text-[10px] break-all">
              {bundle.hashChainAnchorFirst ?? '—'}
            </div>
            <div className="font-semibold text-[10px] uppercase tracking-wide pt-1">
              Last-row anchor
            </div>
            <div className="font-mono text-[10px] break-all">
              {bundle.hashChainAnchorLast ?? '—'}
            </div>
          </div>

          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-[11px] space-y-1.5">
            <div className="font-semibold text-emerald-900 dark:text-emerald-200">
              Verification recipe
            </div>
            <ol className="list-decimal pl-4 space-y-1 text-emerald-800 dark:text-emerald-300">
              <li>Unpack the bundle (gzip → tar-like format).</li>
              <li>Open <code className="font-mono">events.ndjson</code>.</li>
              <li>
                Compute SHA-256(canonical-JSON) of the first and last lines;
                compare against the anchors above.
              </li>
              <li>
                Compute SHA-256 of the unpacked bundle file; verify the Ed25519
                detached signature against the platform public key.
              </li>
              <li>
                The bundle's <code className="font-mono">README.txt</code>
                {' '}restates this recipe.
              </li>
            </ol>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <a
            href={`/api/v1/admin/platform/audit/exports/${bundle.id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            View JSON detail
            <ExternalLink className="h-3 w-3" />
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeyValue({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-muted-foreground text-[11px] uppercase tracking-wide">
        {label}
      </div>
      <div className={cn('text-right break-all', mono && 'font-mono')}>
        {value}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function isoDateOffset(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
